(() => {
  if (customElements.get('tz-bundle-builder')) return;

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
      this.activeSlot = 'top';
      this.purchaseMode = 'both';
      this.selection = {};
      this.imageCache = new Map();
      this.previewRequest = 0;
      this.previewSignature = null;
      this.currentImage = null;
      this.savedSelections = { top: new Map(), bottom: new Map() };
      this.railPositions = { top: 0, bottom: 0 };
      this.money = new Intl.NumberFormat(this.data.locale || 'en', {
        style: 'currency', currency: this.data.currency || 'USD'
      });
      this.ui = {};
      ['products', 'options', 'product-title', 'product-price', 'total', 'add', 'add-label', 'status',
        'photos', 'fallback', 'preview-note', 'panel-title', 'checkout', 'total-label', 'piece-count'].forEach((key) => {
        this.ui[key] = this.querySelector(`[data-${key}]`);
      });
      for (const slot of ['top', 'bottom']) {
        this.data.products[slot] = this.data.products[slot].filter((product) => product.id && product.variants.length);
        const product = this.data.products[slot].find((p) => !p.requiresSellingPlan && p.variants.some((v) => v.available)) || this.data.products[slot][0];
        if (product) this.chooseProduct(slot, product.id);
      }
      if (!this.selection.top && this.selection.bottom) this.purchaseMode = this.activeSlot = 'bottom';
      else if (!this.selection.bottom && this.selection.top) this.purchaseMode = 'top';
      this.addEventListener('click', (event) => this.onClick(event), { signal: this.controller.signal });
      this.addEventListener('change', (event) => this.onChange(event), { signal: this.controller.signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.controller.signal });
      this.ui.products.addEventListener('scroll', () => this.updateRailControls(), { signal: this.controller.signal });
      this.mobileQuery = matchMedia('(max-width: 749px), (max-width: 1000px) and (max-height: 500px) and (orientation: landscape)');
      this.mobileQuery.addEventListener('change', () => { this.syncFullscreen(); this.renderPanel(); }, { signal: this.controller.signal });
      window.addEventListener('resize', () => {
        if (this.optionLayout !== this.optionLayoutKey()) this.renderPanel();
        this.updateRailControls();
      }, { signal: this.controller.signal });
      this.syncFullscreen();
      this.renderPanel();
      this.syncPurchaseControls();
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.previewRequest++;
      this.classList.remove('is-fullscreen');
      document.documentElement.classList.toggle('tz-bb-screen', Boolean(document.querySelector('tz-bundle-builder.is-fullscreen')));
    }

    syncFullscreen() {
      const enabled = this.dataset.popup !== 'true' && this.dataset.fullscreen === 'true' && this.dataset.editor !== 'true' && this.mobileQuery.matches;
      this.classList.toggle('is-fullscreen', enabled);
      document.documentElement.classList.toggle('tz-bb-screen', Boolean(document.querySelector('tz-bundle-builder.is-fullscreen')));
    }

    chooseProduct(slot, id) {
      const product = this.data.products[slot].find((p) => p.id === id);
      if (!product) return;
      const previous = this.selection[slot];
      if (previous) this.savedSelections[slot].set(previous.product.id, [...previous.options]);
      const saved = this.savedSelections[slot].get(id);
      const sizeIndex = product.options.findIndex((name) => this.isSize(name));
      const previousSizeIndex = previous?.product.options.findIndex((name) => this.isSize(name));
      const previousSize = previousSizeIndex >= 0 ? previous.options[previousSizeIndex] : null;
      const variant = (saved && product.variants.find((v) => v.options.every((value, index) => value === saved[index])))
        || (previousSize && product.variants.find((v) => v.available && v.options[sizeIndex] === previousSize))
        || product.variants.find((v) => v.available) || product.variants[0];
      this.selection[slot] = { product, options: [...variant.options] };
    }

    isSize(name) { return ['size', 'veličina', 'velicina', 'taille', 'größe', 'talla'].includes(normalize(name)); }

    optionLayoutKey() { return `${this.mobileQuery.matches}:${window.innerHeight > 740 ? 'roomy' : window.innerHeight >= 560 ? 'compact' : 'short'}`; }

    includedSlots() { return this.purchaseMode === 'both' ? ['top', 'bottom'] : [this.purchaseMode]; }

    syncPurchaseControls() {
      this.dataset.purchase = this.purchaseMode;
      this.querySelectorAll('[data-purchase-mode]').forEach((input) => {
        const slots = input.value === 'both' ? ['top', 'bottom'] : [input.value];
        input.checked = input.value === this.purchaseMode;
        input.disabled = Boolean(this.busy) || slots.some((slot) => !this.selection[slot]);
      });
      this.querySelectorAll('[data-tab]').forEach((tab) => {
        const active = tab.dataset.tab === this.activeSlot;
        tab.disabled = Boolean(this.busy) || !this.selection[tab.dataset.tab];
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active) this.querySelector('[role="tabpanel"]').setAttribute('aria-labelledby', tab.id);
      });
      this.querySelector('.tz-bb__tabs').dataset.active = this.activeSlot;
    }

    setPurchaseMode(mode) {
      if (this.busy || !['both', 'top', 'bottom'].includes(mode)) return;
      if ((mode === 'both' ? ['top', 'bottom'] : [mode]).some((slot) => !this.selection[slot])) return;
      this.purchaseMode = mode;
      if (mode !== 'both') this.setTab(mode);
      this.syncPurchaseControls();
      this.update();
    }

    variant(slot) {
      const selected = this.selection[slot];
      return selected?.product.variants.find((variant) => variant.options.every((value, index) => value === selected.options[index]));
    }

    colorIndex(product) {
      const names = [this.data.colorOption, 'color', 'colour'].map(normalize);
      return product.options.findIndex((name) => names.includes(normalize(name)));
    }

    imageKey(slot) {
      const selected = this.selection[slot];
      if (!selected) return '';
      const color = normalize(selected.options[this.colorIndex(selected.product)]);
      const tags = selected.product.tags.map((tag) => tag.split(':').map((part) => part.trim()))
        .filter((parts) => parts[0] === 'bundle' && (parts.length === 2 || parts.length === 3));
      const specific = tags.find((parts) => parts.length === 3 && normalize(parts[1]) === color);
      // A generic key is safe only when there is no changing color option.
      const colorValues = new Set(selected.product.variants.map((v) => v.options[this.colorIndex(selected.product)]));
      const generic = colorValues.size <= 1 ? tags.find((parts) => parts.length === 2) : null;
      const key = (specific || generic)?.at(-1) || '';
      return /^[a-z0-9][a-z0-9_-]*$/.test(key) && !key.includes('__') ? key : '';
    }

    colorIdentity(slot) {
      const selected = this.selection[slot];
      if (!selected) return '';
      const index = this.colorIndex(selected.product);
      if (index >= 0) return normalize(selected.options[index]).replace(/[-_]+/g, ' ');
      // Single-color products use the existing color-garment file keys.
      const key = this.imageKey(slot);
      const color = key.replace(/[-_](?:t[-_]?shirt|top|tee|shirt|leggings|bottom|pants|shorts|skirt|joggers)$/, '');
      return color !== key ? normalize(color).replace(/[-_]+/g, ' ') : '';
    }

    onClick(event) {
      const cartLink = event.target.closest('[data-open-cart]');
      if (cartLink) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.dataset.cartType === 'drawer' && typeof drawer.toggleDrawerClasses === 'function') {
          event.preventDefault();
          const popup = this.closest('tz-bundle-popup');
          popup?.close();
          // This theme returns focus to mainTrigger when its drawer closes.
          drawer.mainTrigger = popup?.returnFocusElement || cartLink;
          drawer.setActiveElement?.(drawer.mainTrigger);
          if (!drawer.isOpen) drawer.toggleDrawerClasses();
        }
        return;
      }
      const tab = event.target.closest('[data-tab]');
      if (tab) {
        if (this.busy) return;
        this.setTab(tab.dataset.tab);
        return;
      }
      const card = event.target.closest('[data-product]');
      if (card && !this.busy) {
        if (card.dataset.product === this.selection[this.activeSlot]?.product.id) return;
        this.chooseProduct(this.activeSlot, card.dataset.product);
        this.renderPanel();
        this.ui.products.querySelector(`[data-product="${card.dataset.product}"]`)?.focus({ preventScroll: true });
        this.update();
      }
      const arrow = event.target.closest('[data-rail-move]');
      if (arrow && !arrow.disabled) this.moveRail(Number(arrow.dataset.railMove));
      if (event.target.closest('[data-add]')) this.addToCart();
    }

    onChange(event) {
      if (event.target.matches('[data-purchase-mode]')) {
        this.setPurchaseMode(event.target.value);
        return;
      }
      const select = event.target.closest('[data-option]');
      if (!select || this.busy) return;
      const selected = this.selection[this.activeSlot];
      const index = Number(select.dataset.option);
      selected.options[index] = select.value;
      if (this.purchaseMode === 'both' && this.isSize(selected.product.options[index])) {
        const other = this.selection[this.activeSlot === 'top' ? 'bottom' : 'top'];
        const otherSizeIndex = other?.product.options.findIndex((name) => this.isSize(name));
        if (otherSizeIndex >= 0) {
          // Keep the catalog's exact value, even when the two products use different casing.
          // A missing size remains selected so stock validation blocks a mismatched set.
          other.options[otherSizeIndex] = other.product.variants
            .map((variant) => variant.options[otherSizeIndex])
            .find((value) => normalize(value) === normalize(select.value)) ?? select.value;
        }
      }
      this.update();
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const slot = event.key === 'Home' ? 'top' : event.key === 'End' ? 'bottom' : this.activeSlot === 'top' ? 'bottom' : 'top';
        this.setTab(slot);
        this.querySelector(`[data-tab="${slot}"]`).focus();
      }
      // The mobile studio replaces the page; keep keyboard navigation in its visible controls.
      if (event.key === 'Tab' && this.classList.contains('is-fullscreen')) {
        const controls = [...this.querySelectorAll('a[href], button:not(:disabled), select:not(:disabled), input:not(:disabled)')]
          .filter((node) => node.tabIndex >= 0 && node.getClientRects().length);
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
      }
    }

    setTab(slot) {
      if (this.busy || !this.selection[slot]) return;
      if (this.purchaseMode !== 'both' && this.purchaseMode !== slot) {
        this.setPurchaseMode(slot);
        return;
      }
      if (this.activeSlot === slot) return;
      this.railPositions[this.activeSlot] = this.ui.products.scrollLeft;
      this.activeSlot = slot;
      this.querySelector('.tz-bb__tabs').dataset.active = slot;
      this.querySelectorAll('[data-tab]').forEach((tab) => {
        const active = tab.dataset.tab === slot;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active) this.querySelector('[role="tabpanel"]').setAttribute('aria-labelledby', tab.id);
      });
      this.renderPanel();
      this.ui.products.scrollLeft = this.railPositions[slot];
      this.updateRailControls();
      if (!this.reducedMotion()) this.querySelector('[role="tabpanel"]').animate([{ opacity: 0.45, transform: 'translateY(5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 180, easing: 'ease-out' });
      this.updatePrices();
    }

    renderPanel() {
      this.optionLayout = this.optionLayoutKey();
      const slot = this.activeSlot;
      const selected = this.selection[slot];
      this.ui['panel-title'].textContent = slot === 'top' ? 'Pronađi gornji deo' : 'Pronađi donji deo';
      const previousScroll = this.ui.products.scrollLeft;
      if (this.ui.products.dataset.slot !== slot) {
        this.ui.products.replaceChildren();
        this.data.products[slot].forEach((product) => {
        const button = element('button', 'tz-bb__product');
        button.type = 'button';
        button.dataset.product = product.id;
        button.setAttribute('aria-pressed', String(product.id === selected?.product.id));
        button.disabled = Boolean(this.busy);
        button.title = product.title;
        if (product.image) {
          const img = element('img');
          img.src = product.image;
          img.alt = '';
          img.width = 120;
          img.height = 140;
          img.loading = 'lazy';
          button.append(img);
        } else button.append(element('span', 'tz-bb__product-placeholder', slot === 'top' ? 'GORNJI DEO' : 'DONJI DEO'));
        button.append(element('span', '', product.title));
        const check = element('span', 'tz-bb__product-check', '✓');
        check.setAttribute('aria-hidden', 'true');
        button.append(check);
        if (!product.variants.some((v) => v.available)) button.append(element('small', '', 'Rasprodato'));
        this.ui.products.append(button);
        });
        this.ui.products.dataset.slot = slot;
      } else {
        this.ui.products.querySelectorAll('[data-product]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.product === selected?.product.id));
          button.disabled = Boolean(this.busy);
        });
      }
      this.ui.products.scrollLeft = previousScroll;
      this.updateRailControls();
      this.ui.options.replaceChildren();
      this.ui['product-title'].textContent = selected?.product.title || 'Nema izabranih proizvoda';
      if (!selected) return;
      selected.product.options.forEach((name, index) => {
        const values = [...new Set(selected.product.variants.map((variant) => variant.options[index]))];
        const optionLabel = this.isSize(name) ? 'Veličina' : index === this.colorIndex(selected.product) ? 'Boja' : name;
        if (values.length === 1 && values[0] === 'Default Title') return;
        const sidebarSizesFit = values.length <= 4 && selected.product.options.length < 3
          && (window.innerHeight > 740 || window.innerHeight >= 560 && selected.product.options.length === 1 && values.length <= 3);
        if (this.isSize(name) && values.length <= 8 && (!this.mobileQuery.matches || sidebarSizesFit)) {
          const fieldset = element('fieldset', 'tz-bb__option tz-bb__sizes');
          fieldset.append(element('legend', '', optionLabel));
          const choices = element('div', 'tz-bb__size-choices');
          values.forEach((value) => {
            const label = element('label', 'tz-bb__size');
            const radio = element('input');
            radio.type = 'radio';
            radio.name = `${this.querySelector('[role="tabpanel"]').id}-${slot}-${index}`;
            radio.value = value;
            radio.dataset.option = index;
            radio.checked = value === selected.options[index];
            radio.disabled = Boolean(this.busy);
            label.append(radio, element('span', '', value));
            choices.append(label);
          });
          fieldset.append(choices);
          this.ui.options.append(fieldset);
          return;
        }
        const label = element('label', 'tz-bb__option');
        label.append(element('span', '', optionLabel));
        const select = element('select');
        select.dataset.option = index;
        select.disabled = Boolean(this.busy);
        values.forEach((value) => select.append(new Option(value, value, false, value === selected.options[index])));
        if (!values.includes(selected.options[index])) {
          const unavailable = new Option(`${selected.options[index]} — nedostupno`, selected.options[index], true, true);
          unavailable.disabled = true;
          select.append(unavailable);
        }
        label.append(select);
        this.ui.options.append(label);
      });
      this.ui.options.dataset.count = this.ui.options.children.length;
      this.updateOptionAvailability();
    }

    moveRail(direction) {
      const cards = this.ui.products.querySelectorAll('[data-product]');
      if (cards.length < 2) return;
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (step <= 0) return;
      this.ui.products.scrollBy({ left: direction * step, behavior: this.reducedMotion() ? 'instant' : 'smooth' });
    }

    updateRailControls() {
      const rail = this.ui?.products;
      if (!rail) return;
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      this.querySelectorAll('[data-rail-move]').forEach((button) => {
        button.disabled = button.dataset.railMove === '-1' ? rail.scrollLeft <= 1 : rail.scrollLeft >= maxScroll - 1;
      });
    }

    updateOptionAvailability() {
      const selected = this.selection[this.activeSlot];
      if (!selected) return;
      this.ui.options.querySelectorAll('input[data-option]').forEach((input) => {
        const index = Number(input.dataset.option);
        const variant = selected.product.variants.find((v) => v.options.every((value, i) => value === (i === index ? input.value : selected.options[i])));
        input.closest('label').classList.toggle('is-unavailable', !variant?.available);
        input.setAttribute('aria-label', `${input.value}${variant?.available ? '' : ' — nedostupno'}`);
      });
    }

    updatePrices() {
      const activeVariant = this.variant(this.activeSlot);
      this.ui['product-price'].textContent = activeVariant ? this.money.format(activeVariant.price / 100) : 'Nedostupno';
      const variants = this.includedSlots().map((slot) => this.variant(slot));
      this.ui.total.textContent = variants.every(Boolean) ? this.money.format(variants.reduce((sum, v) => sum + v.price, 0) / 100) : '—';
      this.ui['total-label'].textContent = this.purchaseMode === 'both' ? 'Tvoj komplet' : this.purchaseMode === 'top' ? 'Tvoj gornji deo' : 'Tvoj donji deo';
      this.ui['piece-count'].textContent = variants.length === 2 ? '2 komada' : '1 komad';
    }

    update() {
      this.ui.checkout.hidden = true;
      this.updatePrices();
      this.updateOptionAvailability();
      for (const slot of ['top', 'bottom']) {
        const selected = this.selection[slot];
        const color = this.colorIdentity(slot);
        const values = selected?.options.filter((value, index) => value !== 'Default Title' && index !== this.colorIndex(selected.product)) || [];
        const summary = [color ? color.charAt(0).toUpperCase() + color.slice(1) : selected?.product.title, ...values].filter(Boolean).join(' · ');
        this.querySelector(`[data-summary="${slot}"]`).textContent = selected ? summary : slot === 'top' ? 'Izaberi gornji deo' : 'Izaberi donji deo';
      }
      const variants = this.includedSlots().map((slot) => this.variant(slot));
      const subscription = this.includedSlots().some((slot) => this.selection[slot]?.product.requiresSellingPlan);
      this.canAdd = variants.every((v) => v?.available) && !subscription;
      this.ui.add.disabled = !this.canAdd || Boolean(this.busy);
      this.ui['add-label'].textContent = this.purchaseMode === 'both' ? 'Dodaj komplet u korpu' : this.purchaseMode === 'top' ? 'Dodaj gornji deo u korpu' : 'Dodaj donji deo u korpu';
      this.ui.status.textContent = subscription ? 'Ovaj proizvod zahteva pretplatu. Izaberi drugi.' : variants.some((v) => !v) ? 'Izaberi dostupnu kombinaciju opcija.' : !this.canAdd ? 'Ova kombinacija je rasprodata. Izaberi drugu boju ili veličinu.' : 'Dostava i popusti obračunavaju se pri završetku kupovine.';
      this.updatePreview();
    }

    reducedMotion() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }

    loadImage(url) {
      if (this.imageCache.has(url)) return this.imageCache.get(url);
      const promise = new Promise((resolve) => {
        const img = new Image();
        const finish = (result) => { clearTimeout(timer); img.onload = null; img.onerror = null; resolve(result); };
        const timer = setTimeout(() => finish(false), 8000);
        img.onload = async () => {
          try { await img.decode(); } catch { /* onload already confirmed a usable image */ }
          finish(img.naturalWidth > 0);
        };
        img.onerror = () => finish(false);
        img.src = url;
      });
      this.imageCache.set(url, promise);
      return promise;
    }

    productPhoto(slot) {
      const selected = this.selection[slot];
      if (!selected) return null;
      const colorIndex = this.colorIndex(selected.product);
      const matchingImage = selected.product.variants.find((v) => v.image && colorIndex >= 0 && v.options[colorIndex] === selected.options[colorIndex])?.image;
      return this.variant(slot)?.image || matchingImage || selected.product.previewImage || selected.product.image;
    }

    async showPhoto(url, alt, request) {
      if (!url || !await this.loadImage(url)) return false;
      if (request !== this.previewRequest || !this.isConnected) return false;
      if (this.currentImage === url) { this.ui.photos.lastElementChild.alt = alt; return true; }
      const previous = this.ui.photos.lastElementChild;
      this.ui.photos.querySelectorAll('img').forEach((img) => { if (img !== previous) img.remove(); });
      const image = element('img', 'tz-bb__outfit');
      image.src = url;
      image.alt = alt;
      this.ui.photos.append(image);
      this.currentImage = url;
      this.ui.fallback.hidden = true;
      if (!this.reducedMotion()) {
        const animation = image.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
        animation.finished.then(() => previous?.remove()).catch(() => previous?.remove());
      } else previous?.remove();
      return true;
    }

    async resolveCombination(candidates, request) {
      if (!candidates.length) return null;
      if (await this.loadImage(candidates[0])) return candidates[0];
      if (request !== this.previewRequest || !this.isConnected) return null;
      try {
        return await Promise.any(candidates.slice(1).map(async (candidate) => {
          if (await this.loadImage(candidate)) return candidate;
          throw new Error('Slika nije dostupna');
        }));
      } catch { return null; }
    }

    async updatePreview() {
      const topKey = this.imageKey('top');
      const bottomKey = this.imageKey('bottom');
      const key = topKey && bottomKey ? `${topKey}__${bottomKey}` : '';
      const topColor = this.colorIdentity('top');
      const sameColor = Boolean(topColor && topColor === this.colorIdentity('bottom'));
      const photoSlot = this.purchaseMode === 'bottom' ? 'bottom' : 'top';
      const topPhoto = this.productPhoto(photoSlot);
      const file = this.data.files[key];
      const candidates = sameColor || this.purchaseMode !== 'both' ? [] : [...new Set((typeof file === 'string' ? [file] : [file?.preferred, ...(file?.alternates || [])]).filter(Boolean))];
      const signature = JSON.stringify([this.purchaseMode, key, sameColor, topPhoto, candidates]);
      // Sizes that reuse a photograph do not restart the animation or loading state.
      if (signature === this.previewSignature) return;
      this.previewSignature = signature;
      const request = ++this.previewRequest;
      this.dataset.previewKey = key;
      this.dataset.previewState = 'loading';
      this.ui.photos.setAttribute('aria-busy', 'true');
      this.ui['preview-note'].textContent = 'Učitavanje tvoje kombinacije…';
      const topAlt = this.selection[photoSlot]?.product.title || (photoSlot === 'top' ? 'Izabrani gornji deo' : 'Izabrani donji deo');
      const combination = this.resolveCombination(candidates, request);
      // On the first visit, show a single top while the outfit is loading.
      // Later changes retain the current photo until the next one is decoded.
      if (!this.currentImage && topPhoto) await this.showPhoto(topPhoto, topAlt, request);
      const combinationUrl = await combination;
      if (request !== this.previewRequest || !this.isConnected) return;
      let shown;
      if (combinationUrl) {
        const alt = ['top', 'bottom'].map((slot) => {
          const selected = this.selection[slot];
          const color = this.colorIdentity(slot);
          return `${selected.product.title}${color ? ` (${color})` : ''}`;
        }).join(' i ');
        shown = await this.showPhoto(combinationUrl, alt, request);
      } else shown = await this.showPhoto(topPhoto, topAlt, request);
      if (request !== this.previewRequest || !this.isConnected) return;
      this.ui.photos.setAttribute('aria-busy', 'false');
      if (!shown) {
        this.currentImage = null;
        this.ui.photos.replaceChildren();
        this.ui.fallback.hidden = false;
        this.ui.fallback.replaceChildren(element('span', '', 'Slika nije dostupna'));
        this.dataset.previewState = 'missing-image';
        this.ui['preview-note'].textContent = 'Tvoj izbor je sačuvan';
        return;
      }
      this.dataset.previewState = this.purchaseMode !== 'both' ? 'single-product' : combinationUrl ? 'ready' : sameColor ? 'same-color' : 'top-fallback';
      this.ui['preview-note'].textContent = this.purchaseMode !== 'both' ? (photoSlot === 'top' ? 'Samo gornji deo' : 'Samo donji deo') : combinationUrl ? 'Tvoja kombinacija' : 'Prikazan gornji deo · oba dela izabrana';
    }

    async addToCart() {
      if (this.busy || !this.canAdd) return;
      this.busy = true;
      this.syncPurchaseControls();
      this.querySelectorAll('[data-product], [data-option], [data-add]').forEach((node) => { node.disabled = true; });
      this.ui.add.setAttribute('aria-busy', 'true');
      const slots = this.includedSlots();
      const itemLabel = this.purchaseMode === 'both' ? 'komplet' : this.purchaseMode === 'top' ? 'gornji deo' : 'donji deo';
      this.ui['add-label'].textContent = `Dodavanje u korpu: ${itemLabel}…`;
      this.ui.status.textContent = '';
      const root = window.Shopify?.routes?.root || '/';
      const drawer = document.querySelector('cart-drawer');
      const useDrawer = drawer?.dataset.cartType === 'drawer' && typeof drawer.renderContents === 'function';
      const sections = useDrawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
      const group = `set-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const items = slots.map((slot) => ({ id: this.variant(slot).id, quantity: 1, ...(slots.length === 2 ? { properties: { _bundle_builder: group, _bundle_piece: slot } } : {}) }));
      let added = false;
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Dodavanje u korpu nije uspelo. Proveri dostupnost.');
        added = true;
        this.ui['add-label'].textContent = 'Dodato u korpu ✓';
        this.ui.status.textContent = slots.length === 2 ? 'Oba dela su u tvojoj korpi.' : `Tvoj ${itemLabel} je u korpi.`;
        this.ui.checkout.hidden = false;
        if (useDrawer && sections.every((id) => result.sections?.[id])) {
          const popup = this.closest('tz-bundle-popup');
          popup?.close();
          drawer.mainTrigger = popup?.returnFocusElement || this.ui.add;
          drawer.setActiveElement?.(drawer.mainTrigger);
          drawer.classList.remove('is-empty');
          drawer.renderContents(result, !drawer.isOpen);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: result });
        }
      } catch (error) {
        // A rendering failure after a successful cart mutation must never invite a duplicate retry.
        this.ui.status.textContent = added ? `Tvoj ${itemLabel} je dodat u korpu. Završi kupovinu putem linka ispod.` : `${error instanceof TypeError ? 'Veza je prekinuta.' : error.message || 'Došlo je do greške.'} Proveri korpu pre ponovnog pokušaja.`;
        if (!added) this.ui['add-label'].textContent = 'Pokušaj ponovo';
        this.ui.checkout.hidden = false;
      } finally {
        this.busy = false;
        this.syncPurchaseControls();
        this.querySelectorAll('[data-product], [data-option]').forEach((node) => { node.disabled = false; });
        this.ui.add.disabled = !this.canAdd;
        this.ui.add.setAttribute('aria-busy', 'false');
      }
    }
  }

  customElements.define('tz-bundle-builder', BundleBuilder);
})();
